import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, User } from 'lucide-react';
import { getVenueQuestions, createVenueQuestion } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';

const VenueQA = ({ venueId, venueName, initialQuestions }) => {
  const [questions, setQuestions] = useState(initialQuestions || []);
  const [loading, setLoading] = useState(!initialQuestions);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    askedBy: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!initialQuestions) loadQuestions();
  }, [venueId]);

  const loadQuestions = async () => {
    try {
      const response = await getVenueQuestions(venueId);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question.trim()) {
      alert('Lütfen bir soru yazın');
      return;
    }

    try {
      setSubmitting(true);
      await createVenueQuestion(venueId, {
        question: formData.question,
        askedBy: formData.askedBy || 'Anonim'
      });
      
      alert('Sorunuz alındı! Mekan sahibi en kısa sürede cevaplayacaktır.');
      setFormData({ question: '', askedBy: '' });
      setShowForm(false);
      // Reload questions (though new ones won't show until answered)
      loadQuestions();
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Soru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-pink-600" />
              <span>Sıkça Sorulan Sorular</span>
              {questions.length > 0 && (
                <span className="text-sm font-normal text-gray-500">
                  ({questions.length} soru)
                </span>
              )}
            </CardTitle>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Yorum Yap
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Question Form */}
          {showForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-pink-200">
              <h4 className="font-semibold text-lg mb-4">
                {venueName} hakkında soru sor
              </h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sorunuz *
                  </label>
                  <textarea
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    rows="3"
                    placeholder="Örn: Mekanınız kaç kişilik?"
                    required
                    maxLength="500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.question.length}/500 karakter
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İsminiz (İsteğe bağlı)
                  </label>
                  <input
                    type="text"
                    value={formData.askedBy}
                    onChange={(e) => setFormData({ ...formData, askedBy: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Anonim"
                    maxLength="100"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-pink-600 hover:bg-pink-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Gönderiliyor...' : 'Soruyu Gönder'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Questions List */}
          {questions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg">Henüz soru sorulmamış.</p>
              <p className="text-sm mt-1">İlk soruyu siz sorun!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((qa) => (
                <div
                  key={qa.id}
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  {/* Question */}
                  <div className="flex gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-pink-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{qa.askedBy}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(qa.createdAt).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <p className="text-gray-800">{qa.question}</p>
                    </div>
                  </div>

                  {/* Answer */}
                  {qa.answer && (
                    <div className="ml-13 pl-4 border-l-2 border-pink-300 bg-pink-50 p-3 rounded-r-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-pink-700">
                          {venueName} - Cevap
                        </span>
                        {qa.answeredAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(qa.answeredAt).toLocaleDateString('tr-TR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700">{qa.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VenueQA;
